/**
 * Detects a Vite/browser dynamic-import failure — the classic "user has
 * a stale tab open, we redeployed, the hashed chunk filename they're
 * trying to fetch is 404 on the CDN" footgun.
 *
 * The error surfaces as a TypeError with a message that includes both
 * "Failed to fetch dynamically imported module" and the offending URL.
 * Firefox/Safari phrase it slightly differently, so match a few variants.
 *
 * Also fires `vite:preloadError` on `window` ahead of the throw — that's
 * the canonical hook for an app-wide auto-recovery (see main.tsx).
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message ?? '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    // Some bundlers surface chunk failures as "Loading chunk N failed"
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  );
}

/**
 * Hard-reload that BUSTS a stale, edge-cached `index.html`.
 *
 * The chunk-404 footgun is recursive: a plain `location.reload()` re-requests
 * the same URL, the CDN serves the same cached (old-hash) document, the app
 * asks for the same moved chunk, it 404s again → the user is trapped on the
 * "New version available / Reload" screen forever. Bumping a throwaway `_v`
 * query param changes the URL the CDN keys its cache on, forcing a fresh
 * origin fetch of the document (whose `<script>` tags then point at the
 * chunks that actually exist). The param is inert to the router and gets
 * dropped on the next clean navigation.
 *
 * Uses `Date.now()` (browser, not a workflow script) so each attempt is a
 * fresh cache key even if the user lands back here within the same second.
 */
export function reloadForNewBuild(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    // URL/replace unavailable (very old/locked-down env) — best-effort plain reload.
    window.location.reload();
  }
}
